import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { User, Lock, Eye, EyeOff, Shield, Mail, KeyRound, Edit2, Plus, Camera } from 'lucide-react';

const ProfilePage = () => {
    const { api, user, refreshUser } = useAuth();
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pinLoading, setPinLoading] = useState(false);
    const [showPinForm, setShowPinForm] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef(null);
    
    // PIN length based on role (ADMIN = 6, others = 4)
    const pinLength = user?.role === 'ADMIN' ? 6 : 4;
    
    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    
    const [pinForm, setPinForm] = useState({
        current_password: '',
        new_pin: '',
        confirm_pin: ''
    });

    // Handle photo upload
    const handlePhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Veuillez sélectionner une image');
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('L\'image ne doit pas dépasser 5 Mo');
            return;
        }
        
        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            await api().post('/staff/upload-photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success('Photo de profil mise à jour');
            if (refreshUser) {
                await refreshUser();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
        } finally {
            setUploadingPhoto(false);
        }
    };

    // Toggle PIN visibility (auto-hide after 3 seconds)
    const handleShowPin = () => {
        setShowPin(true);
        setTimeout(() => setShowPin(false), 3000);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        
        if (passwordForm.new_password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        setLoading(true);
        try {
            await api().post('/auth/change-password', {
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password
            });
            toast.success('Mot de passe modifié avec succès');
            setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePIN = async (e) => {
        e.preventDefault();
        
        if (pinForm.new_pin !== pinForm.confirm_pin) {
            toast.error('Les codes PIN ne correspondent pas');
            return;
        }
        
        const pinRegex = new RegExp(`^\\d{${pinLength}}$`);
        if (!pinRegex.test(pinForm.new_pin)) {
            toast.error(`Le code PIN doit contenir exactement ${pinLength} chiffres`);
            return;
        }
        
        setPinLoading(true);
        try {
            await api().put('/auth/update-pin', {
                current_password: pinForm.current_password,
                pin_code: pinForm.new_pin
            });
            toast.success('Code PIN modifié avec succès');
            setPinForm({ current_password: '', new_pin: '', confirm_pin: '' });
            setShowPinForm(false);
            // Refresh user data to get updated PIN
            if (refreshUser) {
                await refreshUser();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de code PIN');
        } finally {
            setPinLoading(false);
        }
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            'DIRECTEUR': 'bg-amber-500',
            'DIRECTEUR_ADJOINT': 'bg-blue-500',
            'CHEF_CUISINE': 'bg-green-500',
            'CUISINIER': 'bg-orange-500',
            'SERVEUR': 'bg-purple-500',
            'EMPLOYE': 'bg-gray-500'
        };
        return colors[role] || 'bg-gray-500';
    };

    const getRoleLabel = (role) => {
        const labels = {
            'DIRECTEUR': 'Directeur',
            'DIRECTEUR_ADJOINT': 'Directeur Adjoint',
            'CHEF_CUISINE': 'Chef Cuisine',
            'CUISINIER': 'Cuisinier',
            'SERVEUR': 'Serveur',
            'EMPLOYE': 'Employé'
        };
        return labels[role] || role;
    };

    return (
        <div className="space-y-6 max-w-2xl mx-auto" data-testid="profile-page">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Mon Profil</h1>
                <p className="text-gray-500 dark:text-gray-400">Gérez vos informations personnelles et votre mot de passe</p>
            </div>

            {/* User Info Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Informations du compte
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        {/* Profile Photo with + icon */}
                        <div className="relative">
                            <div 
                                className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center overflow-hidden cursor-pointer group border-4 border-white dark:border-gray-700 shadow-lg"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {user?.photo_url ? (
                                    <img 
                                        src={user.photo_url} 
                                        alt={user.name} 
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <User className="w-10 h-10 text-green-600 dark:text-green-400" />
                                )}
                                {/* + overlay - always visible at bottom right */}
                                <div className="absolute bottom-0 right-0 w-6 h-6 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-md">
                                    {uploadingPhoto ? (
                                        <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Plus className="w-5 h-5 text-blue-600 stroke-[3]" />
                                    )}
                                </div>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                className="hidden"
                            />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold">{user?.name || 'Utilisateur'}</h3>
                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                <Mail className="w-4 h-4" />
                                {user?.email}
                            </div>
                        </div>
                        <Badge className={getRoleBadgeColor(user?.role)}>
                            <Shield className="w-3 h-3 mr-1" />
                            {getRoleLabel(user?.role)}
                        </Badge>
                    </div>
                    
                    {/* PIN Code - Hidden by default, visible on click */}
                    {user?.pin_code ? (
                        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                            <div className="flex items-center gap-3">
                                <KeyRound className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                <div>
                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Code PIN</p>
                                    <button
                                        onClick={handleShowPin}
                                        className="text-lg font-mono font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors flex items-center gap-2"
                                        data-testid="toggle-pin-visibility"
                                    >
                                        {showPin ? user.pin_code : '••••'}
                                        {!showPin && <Eye className="w-4 h-4 opacity-50" />}
                                    </button>
                                    {showPin && (
                                        <p className="text-[10px] text-blue-500">Masqué automatiquement dans 3s</p>
                                    )}
                                </div>
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setShowPinForm(!showPinForm)}
                                data-testid="edit-pin-button"
                            >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Modifier
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <KeyRound className="w-5 h-5 text-yellow-600" />
                                <div>
                                    <p className="text-sm font-medium text-yellow-900">Code PIN non défini</p>
                                    <p className="text-xs text-yellow-700">Nécessaire pour le pointage et le déverrouillage de la caisse</p>
                                </div>
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm"
                                className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                                onClick={() => setShowPinForm(true)}
                                data-testid="create-pin-button"
                            >
                                <KeyRound className="w-4 h-4 mr-1" />
                                Créer un PIN
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Change PIN Form - Collapsible */}
            {showPinForm && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5" />
                            {user?.pin_code ? 'Modifier le code PIN' : 'Créer un code PIN'}
                        </CardTitle>
                        <CardDescription>
                            Le code PIN à {pinLength} chiffres est utilisé pour le pointage et le déverrouillage de la caisse
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleChangePIN} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="pin_current_password">Mot de passe actuel (pour vérification)</Label>
                                <Input
                                    id="pin_current_password"
                                    type="password"
                                    value={pinForm.current_password}
                                    onChange={(e) => setPinForm({...pinForm, current_password: e.target.value})}
                                    required
                                    placeholder="Entrez votre mot de passe"
                                    data-testid="pin-current-password-input"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="new_pin">Nouveau code PIN</Label>
                                    <Input
                                        id="new_pin"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={pinLength}
                                        value={pinForm.new_pin}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, pinLength);
                                            setPinForm({...pinForm, new_pin: val});
                                        }}
                                        required
                                        placeholder={`${pinLength} chiffres`}
                                        className="text-center text-lg font-mono tracking-widest"
                                        data-testid="new-pin-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="confirm_pin">Confirmer le PIN</Label>
                                    <Input
                                        id="confirm_pin"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={pinLength}
                                        value={pinForm.confirm_pin}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, pinLength);
                                            setPinForm({...pinForm, confirm_pin: val});
                                        }}
                                        required
                                        placeholder={`${pinLength} chiffres`}
                                        className="text-center text-lg font-mono tracking-widest"
                                        data-testid="confirm-pin-input"
                                    />
                                </div>
                            </div>
                            
                            {pinForm.new_pin && pinForm.confirm_pin && 
                             pinForm.new_pin !== pinForm.confirm_pin && (
                                <p className="text-sm text-red-500">Les codes PIN ne correspondent pas</p>
                            )}
                            
                            {pinForm.new_pin && pinForm.new_pin.length < pinLength && (
                                <p className="text-sm text-yellow-600">Le code PIN doit contenir {pinLength} chiffres</p>
                            )}
                            
                            <div className="flex gap-2">
                                <Button 
                                    type="submit" 
                                    disabled={pinLoading || pinForm.new_pin.length !== pinLength}
                                    className="flex-1"
                                    data-testid="save-pin-button"
                                >
                                    {pinLoading ? 'Enregistrement...' : 'Enregistrer le PIN'}
                                </Button>
                                <Button 
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowPinForm(false);
                                        setPinForm({ current_password: '', new_pin: '', confirm_pin: '' });
                                    }}
                                >
                                    Annuler
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Change Password Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        Changer le mot de passe
                    </CardTitle>
                    <CardDescription>
                        Assurez-vous d'utiliser un mot de passe sécurisé d'au moins 6 caractères
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password">Mot de passe actuel</Label>
                            <div className="relative">
                                <Input
                                    id="current_password"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={passwordForm.current_password}
                                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                                    required
                                    data-testid="current-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="new_password">Nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="new_password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={passwordForm.new_password}
                                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                                    required
                                    minLength={6}
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="confirm_password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={passwordForm.confirm_password}
                                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                                    required
                                    minLength={6}
                                    data-testid="confirm-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {passwordForm.new_password && passwordForm.confirm_password && 
                             passwordForm.new_password !== passwordForm.confirm_password && (
                                <p className="text-sm text-red-500">Les mots de passe ne correspondent pas</p>
                            )}
                        </div>
                        
                        <Button 
                            type="submit" 
                            disabled={loading}
                            className="w-full"
                            data-testid="change-password-submit"
                        >
                            {loading ? 'Modification...' : 'Changer le mot de passe'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ProfilePage;
